import React, { useState, useEffect, useMemo } from "react";
import { Box, Divider, Typography } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { fillParent, scrollingBox, footerFlex } from "utils/base-styles";
import useCollection from "features/Collections/hooks/use-collection.hook";

import AccessSidebar from "features/AccessSidebar";
import BookmarksTable from "features/Collections/components/Table";
import Header from "features/Collections/components/Header";
import Edit from "features/Collections/components/Popups/Edit";
import Delete from "features/Collections/components/Popups/Delete";
import Archive from "features/Collections/components/Popups/Archive";
import Unarchive from "features/Collections/components/Popups/Unarchive";
import Copy from "features/Collections/components/Popups/Copy";
import Footer from "components/layout/Footer";
import { useIsTab } from "hooks/is-tab.hook";
import CollectionsTableGrid from "features/Collections/components/CollectionTable";
import BookmarkHeader from "./BookmarkHeader";
import MainPage from "features/WorkCited/components/MainPage";
import WorkCited from "features/WorkCited";
import CollectionNotes from "features/Collections/components/CollectionNotes";
import { useDispatch, useSelector } from "react-redux";
import { setIsReadOnlyView, setIsTeacherReviewView } from "context/features/dataSlice";
import { toast } from "react-toastify";
import { SUBMISSION_FOLDERS_UPDATED_EVENT, useStudentSubmissions } from "features/Submissions/hooks";
import { getSubmissionContext, reviewSubmission, submitCollection } from "features/Submissions/services";
import { SubmitCollectionDialog, TeacherNotesView } from "features/Submissions/components";

export default function Collection({ teacherMode = false }) {
  const [loading, setLoading] = useState(true);
  const [selectedCollectionOption, setSelectedCollectionOption] =
    useState("bookmarks");

  const {
    currentCollection,
    bookmarks,
    filteredBookmarks,
    setFilteredBookmarks,
    getUsersList,
    shareCollectionFolder,
    removeSharedUser,
    makeCollectionPublic,
    handleDeleteCollection,
    handleRenameCollection,
    handleCopyCollection,
    handleArchiveCollection,
    handleRemoveAllSharedUsers,
    handleCollectionOverview,
    setBookmarks,
    markdownNotes,
    setMarkdownNotes,
    saveCollectionWithNotes,
    markCollectionSubmitted,
    getData,
    handleGetData,
    collectionAccessError,
  } = useCollection(setLoading, selectedCollectionOption);

  const [edit, setEdit] = useState(false);
  const [deletePopup, setDeletePopup] = useState(false);
  const [archivePopup, setArchivePopup] = useState(false);
  const [unarchivePopup, setUnarchivePopup] = useState(false);
  const [copyConfirmPopup, setCopyConfirmPopup] = useState(false);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [accessPopup, setAccessPopup] = useState(false);
  const [filter, setFilter] = useState("");
  const [name, setName] = useState("");
  const [collectionOverview, setCollectionOverview] = useState({});
  const [sortCriteria, setSortCriteria] = useState("dateAdded");
  const [sortDirection, setSortDirection] = useState("asc");
  const { id, workcitedid } = useParams();
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [workCitedName, setWorkCitedName] = useState("");
  const [workCitedFormatStyle, setWorkCitedFormatStyle] = useState("");

  const isTab = useIsTab();
  const dispatch = useDispatch();
  const location = useLocation();
  const { pathname } = location;
  // Determined from the URL rather than currentCollection.isArchived_ysn, which can
  // lag or go missing if the collection is matched from the non-archived list first.
  const isArchivedRoute = pathname.includes("/collections/archive/");
  const {isSidebarOpen, isBookmarkSidebar} = useSelector((state) => state.data);
  const {
    folders: eligibleSubmissionFolders,
    submissions,
    refresh: refreshSubmissions,
  } = useStudentSubmissions(teacherMode ? null : id);
  const [submitPopup, setSubmitPopup] = useState(false);
  const [submitSaving, setSubmitSaving] = useState(false);
  const [teacherSubmission, setTeacherSubmission] = useState(null);
  const [submissionContextError, setSubmissionContextError] = useState(false);

  const teacherSubmissionData = useMemo(
    () => ({
      ...(location.state?.submission || {}),
      ...(teacherSubmission || {}),
    }),
    [location.state, teacherSubmission]
  );

  // The student-facing submission endpoints return teacherNote_chr as null, but
  // GetCollections carries the note on the collection itself, so read it from there.
  const studentSubmission = useMemo(() => {
    if (teacherMode) return null;
    const submission = submissions[0];
    if (!submission) return submission;
    const published = Boolean(currentCollection?.isTeacherNotePublished_ysn);
    return {
      ...submission,
      teacherNote_chr:
        submission.teacherNote_chr ||
        (published ? currentCollection?.teacherNote_chr : null) ||
        null,
      isTeacherNotePublished_ysn:
        Boolean(submission.isTeacherNotePublished_ysn) || published,
    };
  }, [currentCollection, submissions, teacherMode]);

  const currentSubmission = teacherMode
    ? teacherSubmissionData
    : studentSubmission;
  const isSubmitted =
    teacherMode ||
    Boolean(currentCollection?.isSubmitted_ysn) ||
    submissions.length > 0;
  const collectionSharedWithEmails =
    teacherMode
      ? currentSubmission?.collectionSharedWithEmails ||
        currentSubmission?.collectionSharedWithUserName ||
        []
      : [];
  const teacherSubmissionOwners = useMemo(() => {
    const owner = {
      email:
        currentSubmission?.studentEmail_chr ||
        currentSubmission?.ownerEmail_chr ||
        location.state?.submission?.studentEmail_chr ||
        null,
      userId:
        currentSubmission?.studentUserID_chr ||
        currentSubmission?.ownerUserID_chr ||
        location.state?.submission?.studentUserID_chr ||
        null,
      avatarUrl: currentSubmission?.userAvatarUrl_chr || null,
    };

    if (!teacherMode || collectionSharedWithEmails.length === 0) {
      return owner.email ? [owner] : [];
    }

    const sharedIds = currentSubmission?.collectionSharedWithUserId || [];
    const sharedAvatars =
      currentSubmission?.collectionSharedWithUserIdByAvatarUrl || [];

    const seenEmails = new Set();
    return [
      owner,
      ...collectionSharedWithEmails.map((email, index) => ({
        email,
        userId: sharedIds[index] || email,
        avatarUrl: sharedAvatars[index] || null,
      })),
    ].filter((item) => {
      if (item.email == null || seenEmails.has(item.email)) {
        return false;
      }
      seenEmails.add(item.email);
      return true;
    });
  }, [
    collectionSharedWithEmails,
    currentSubmission,
    location.state,
    teacherMode,
  ]);
  const teacherCollectionOverview = useMemo(() => {
    if (!teacherMode || teacherSubmissionOwners.length === 0) {
      return collectionOverview;
    }

    const [primaryOwner] = teacherSubmissionOwners;
    const additionalOwnerCount = teacherSubmissionOwners.length - 1;
    return {
      ...collectionOverview,
      collectionSharedWithUsersDisplay:
        additionalOwnerCount > 0
          ? `${primaryOwner.email} +${additionalOwnerCount} ${additionalOwnerCount === 1 ? "other" : "others"}`
          : primaryOwner.email,
      collectionSharedWithUserId: teacherSubmissionOwners.map(
        (owner, index) => owner.userId || `submission-owner-${index}`
      ),
      collectionSharedWithUserIdByAvatarUrl: teacherSubmissionOwners.map(
        (owner) => owner.avatarUrl
      ),
    };
  }, [collectionOverview, currentSubmission, teacherMode, teacherSubmissionOwners]);
  const submissionFolderID =
    location.state?.submissionFolderID ||
    currentSubmission?.submissionFolderID_lng ||
    currentSubmission?.submissionFolderID_ids ||
    currentSubmission?.submissionFolderID;

  const studentEmail =
    currentSubmission?.studentEmail_chr || location.state?.submission?.studentEmail_chr || null;
  const studentUserId =
    currentSubmission?.studentUserID_chr || location.state?.submission?.studentUserID_chr || null;

  // Every collection surface is read-only while a teacher reviews a submission, the
  // same way it is for an archived collection.
  useEffect(() => {
    dispatch(setIsReadOnlyView(teacherMode || isArchivedRoute));
    dispatch(setIsTeacherReviewView(teacherMode));
    return () => {
      dispatch(setIsReadOnlyView(false));
      dispatch(setIsTeacherReviewView(false));
    };
  }, [dispatch, teacherMode, isArchivedRoute]);

  // The teacher does not own this collection, so it is absent from their own
  // collection list and `currentCollection` never gets a name. GetCollectionSource
  // returns the name on every row, and the submission row carries it too.
  const headerCollection = useMemo(() => {
    if (!teacherMode) return currentCollection;
    // Built from scratch rather than spread over `currentCollection`, which still holds
    // whichever of the teacher's own collections they last opened.
    return {
      collectionName_chr:
        location.state?.submission?.collectionName_chr ||
        bookmarks[0]?.collectionName_chr ||
        "",
      // The teacher has no sharing, publishing, or archiving rights here.
      isShared_ysn: teacherSubmissionOwners.length > 1,
      isPublic_ysn: false,
      isArchived_ysn: false,
    };
  }, [
    bookmarks,
    currentCollection,
    location.state,
    teacherMode,
    teacherSubmissionOwners,
  ]);

  const handleFilterBookmark = () => {
    const filteredBookmark = bookmarks.filter(
      (bookmark) =>
        bookmark.title_chr.toLowerCase().includes(filter.toLowerCase()) ||
        bookmark.sourceName_chr.toLowerCase().includes(filter.toLowerCase())
    );
    setFilteredBookmarks(filteredBookmark);
  };

  const fetchCollectionOverview = async () => {
    try {
      const data = await handleCollectionOverview();
      setCollectionOverview(data);
    } catch (e) {
      console.error(e);
    }
  };

  // Header's shared-with display (collectionOverview.collectionSharedWithUsersDisplay)
  // only comes from this overview call, not from the collections list refetch that
  // shareCollectionFolder/removeSharedUser already do — so it stays stale (showing just
  // "@" with no name) after an access change until this refreshes too.
  const handleShareCollectionFolder = async (payload) => {
    await shareCollectionFolder(payload);
    await fetchCollectionOverview();
  };

  const handleRemoveSharedUser = async (id, userToRemove) => {
    await removeSharedUser(id, userToRemove);
    await fetchCollectionOverview();
  };

  const handleRemoveAllSharedUsersAndRefresh = async () => {
    await handleRemoveAllSharedUsers();
    await fetchCollectionOverview();
  };

  const sortData = (criteria, direction, data) => {
    const sortedData = [...data].sort((a, b) => {
      let valA, valB;
      switch (criteria) {
        case "dateAdded":
          valA = new Date(a.createdDateTime_dtm);
          valB = new Date(b.createdDateTime_dtm);
          break;
        case "publishedDate":
          valA = new Date(a.sourcePublishedDateTime_dtm);
          valB = new Date(b.sourcePublishedDateTime_dtm);
          break;
        case "newsGuardRating":
          valA = a.score ? parseFloat(a.score) : -Infinity;
          valB = b.score ? parseFloat(b.score) : -Infinity;
          break;
        case "title":
          valA = a.title_chr.toLowerCase();
          valB = b.title_chr.toLowerCase();
          break;
        case "source":
          valA = a.sourceName_chr.toLowerCase();
          valB = b.sourceName_chr.toLowerCase();
          break;
        default:
          return 0;
      }

      if (valA < valB) return direction === "asc" ? -1 : 1;
      if (valA > valB) return direction === "asc" ? 1 : -1;
      return 0;
    });
    return sortedData;
  };

  const handleSort = (criteria) => {
    const newDirection =
      sortCriteria === criteria && sortDirection === "asc" ? "desc" : "asc";
    setSortCriteria(criteria);
    setSortDirection(newDirection);
    const dataToSort = filter ? filteredBookmarks : bookmarks;
    const sortedBookmarks = sortData(criteria, newDirection, dataToSort);
    if (filter) {
      setFilteredBookmarks(sortedBookmarks);
    } else {
      setBookmarks(sortedBookmarks);
    }
  };

  useEffect(() => {
    handleFilterBookmark();
  }, [filter]);

  useEffect(() => {
    fetchCollectionOverview();
  }, [id]);

  useEffect(() => {
    if(pathname.includes("teacher-notes")){
      setSelectedCollectionOption("teachernotes");
    }else if(pathname.includes("work-cited")){
      setSelectedCollectionOption("workcited");
    }else if(pathname.includes("collection-notes")){
      setSelectedCollectionOption("collectionnotes");
    }
    else{
      setSelectedCollectionOption("bookmarks");
    }
  }, [pathname]);

  // GetSubmissionContext takes the submission ID and nothing else, and its response is
  // the source of truth for the submission — no merging of whatever the folder page
  // happened to pass through router state.
  useEffect(() => {
    if (teacherMode && submissionId) {
      setSubmissionContextError(false);
      getSubmissionContext(Number(submissionId))
        .then(setTeacherSubmission)
        .catch((error) => {
          toast.error(error.message || "Unable to load submission details.");
          setSubmissionContextError(true);
        });
    }
  }, [submissionId, teacherMode]);

  const handleSubmitCollection = async (submissionFolderID) => {
    setSubmitSaving(true);
    try {
      await submitCollection(Number(id), submissionFolderID);
      toast.success("Collection submitted.");
      setSubmitPopup(false);
      markCollectionSubmitted(id);
      await refreshSubmissions();
      window.dispatchEvent(new Event(SUBMISSION_FOLDERS_UPDATED_EVENT));
    } catch (error) {
      toast.error(error.message || "Unable to submit collection.");
    } finally {
      setSubmitSaving(false);
    }
  };

  const handleTeacherReview = async (isReviewed) => {
    if (!teacherSubmission) return;
    try {
      const updated = await reviewSubmission({
        submissionID: Number(submissionId),
        isReviewed,
        teacherNote: teacherSubmission.teacherNote_chr || teacherSubmission.teacherNote || "",
      });
      setTeacherSubmission((previous) => ({ ...previous, ...(updated || {}), isReviewed_ysn: isReviewed }));
    } catch (error) {
      toast.error(error.message || "Unable to update review status.");
    }
  };

  const handleTeacherNotesSave = async (teacherNote) => {
    if (!teacherSubmission) return;
    try {
      const updated = await reviewSubmission({
        submissionID: Number(submissionId),
        isReviewed: Boolean(teacherSubmission.isReviewed_ysn),
        teacherNote,
      });
      setTeacherSubmission((previous) => ({ ...previous, ...(updated || {}), teacherNote_chr: teacherNote }));
    } catch (error) {
      toast.error(error.message || "Unable to save teacher notes.");
    }
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if(id)
    handleGetData();
  }, [id, isSidebarOpen, isBookmarkSidebar]);

  if (collectionAccessError) {
    return (
      <Box
        sx={{
          ...fillParent,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 1,
          p: 4,
        }}
      >
        <Typography variant="h6">Collection not found</Typography>
        <Typography variant="body1" color="text.secondary">
          Either you are not part of this collection, or it no longer exists.
        </Typography>
      </Box>
    );
  }

  if (submissionContextError) {
    return (
      <Box
        sx={{
          ...fillParent,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 1,
          p: 4,
        }}
      >
        <Typography variant="h6">Submission not found</Typography>
        <Typography variant="body1" color="text.secondary">
          This submission no longer exists, or it may have been removed.
        </Typography>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Box
        sx={{
          ...fillParent,
          ...scrollingBox,
          ...footerFlex,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateAreas: !isTab
              ? '"header header header" "list list list" "footer footer footer"'
              : '"header" "list" "footer"',
            maxWidth: "100%",
            mt: 3,
          }}
        >
          {!(workcitedid || pathname.includes("work-cited/create")) ? (
            <React.Fragment>
              <Box
                sx={{
                  gridArea: "header",
                }}
              >
                <Header
                  isPublic={false}
                  setAccessPopup={setAccessPopup}
                  collection={headerCollection}
                  length={filter ? filteredBookmarks.length : bookmarks.length}
                  setFilter={setFilter}
                  filter={filter}
                  setEdit={setEdit}
                  setDeletePopup={setDeletePopup}
                  setArchivePopup={setArchivePopup}
                  setUnarchivePopup={setUnarchivePopup}
                  setCopyConfirmPopup={setCopyConfirmPopup}
                  collectionOverview={teacherCollectionOverview}
                  setName={setName}
                  onSort={handleSort}
                  sortCriteria={sortCriteria}
                  sortDirection={sortDirection}
                  loading={loading}
                  isSubmitted={isSubmitted}
                  submitDisabled={teacherMode || eligibleSubmissionFolders.length === 0 || isSubmitted}
                  onSubmit={() => setSubmitPopup(true)}
                  deleteDisabled={isSubmitted}
                  isTeacherMode={teacherMode}
                  ownerLabel={
                    teacherMode && teacherSubmissionOwners.length <= 1
                      ? studentEmail
                      : null
                  }
                  ownerUserId={
                    teacherMode && teacherSubmissionOwners.length <= 1
                      ? studentUserId
                      : null
                  }
                  onBack={() => navigate(
                    submissionFolderID
                      ? `/collections/submissions/${submissionFolderID}`
                      : "/collections/submissions"
                  )}
                />
                <BookmarkHeader
                  setSelectedCollectionOption={setSelectedCollectionOption}
                  selectedCollectionOption={selectedCollectionOption}
                  isArchived={isArchivedRoute}
                  isSubmitted={isSubmitted}
                  isTeacherMode={teacherMode}
                />
              </Box>

              <Box sx={{ gridArea: "list", overflowX: "auto" }}>
                {selectedCollectionOption === "workcited" ? (
                  <MainPage
                    setWorkCitedName={setWorkCitedName}
                    setWorkCitedFormatStyle={setWorkCitedFormatStyle}
                    readOnly={teacherMode || isArchivedRoute}
                  />
                ) : selectedCollectionOption === "collectionnotes" ? (
                  <CollectionNotes
                    markdownSummary={markdownNotes}
                    setMarkdownSummary={setMarkdownNotes}
                    saveCollectionFolder={saveCollectionWithNotes}
                    handleCollectionOverview={handleCollectionOverview}
                    readOnly={isArchivedRoute || teacherMode}
                    placeholder={
                      teacherMode
                        ? "This student hasn't added any notes to this collection."
                        : undefined
                    }
                  />
                ) : selectedCollectionOption === "teachernotes" ? (
                  <TeacherNotesView
                    submission={currentSubmission}
                    readOnly={!teacherMode}
                    onReviewedChange={handleTeacherReview}
                    onSave={handleTeacherNotesSave}
                  />
                ) : (
                  <CollectionsTableGrid
                    recentCollectionSource={
                      filter ? filteredBookmarks : bookmarks
                    }
                    loading={loading}
                    filter={filter}
                    teacherMode={teacherMode}
                  />
                )}
              </Box>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <Box
                sx={{
                  gridArea: "header",
                }}
              ></Box>
              <Box
                sx={{
                  gridArea: "list",
                  overflowX: "auto",
                }}
              >
                  <WorkCited
                  workCitedName={workCitedName}
                  setWorkCitedName={setWorkCitedName}
                  setWorkCitedFormatStyle={setWorkCitedFormatStyle}
                    workCitedFormatStyle={workCitedFormatStyle}
                    readOnly={teacherMode || isArchivedRoute}
                />
              </Box>
            </React.Fragment>
          )}
        </Box>
        <Footer sx={{ gridArea: "footer", px: 0 }} />
      </Box>
      {accessPopup && (
        <AccessSidebar
          accessPopup={accessPopup}
          setAccessPopup={setAccessPopup}
          shareCollectionFolder={handleShareCollectionFolder}
          getUsersList={getUsersList}
          removeSharedUser={handleRemoveSharedUser}
          collection={currentCollection}
          length={bookmarks.length}
          makeCollectionPublic={makeCollectionPublic}
          handleRemoveAllSharedUsers={handleRemoveAllSharedUsersAndRefresh}
          readOnly={teacherMode || isArchivedRoute}
        />
      )}
      {edit && (
        <Edit
          edit={edit}
          setEdit={setEdit}
          name={name}
          handleRenameCollection={handleRenameCollection}
        />
      )}
      {deletePopup && (
        <Delete
          deletePopup={deletePopup}
          setDeletePopup={setDeletePopup}
          handleDeleteCollection={handleDeleteCollection}
        />
      )}
      {archivePopup && (
        <Archive
          archivePopup={archivePopup}
          setArchivePopup={setArchivePopup}
          collection={currentCollection}
          handleArchiveCollection={handleArchiveCollection}
        />
      )}
      {unarchivePopup && (
        <Unarchive
          unarchivePopup={unarchivePopup}
          setUnarchivePopup={setUnarchivePopup}
          collection={currentCollection}
          handleArchiveCollection={handleArchiveCollection}
        />
      )}
      {copyConfirmPopup && (
        <Copy
          copyConfirmPopup={copyConfirmPopup}
          setCopyConfirmPopup={setCopyConfirmPopup}
          collection={currentCollection}
          handleCopyCollection={handleCopyCollection}
        />
      )}
      {!teacherMode && (
        <SubmitCollectionDialog
          open={submitPopup}
          folders={eligibleSubmissionFolders}
          saving={submitSaving}
          onClose={() => setSubmitPopup(false)}
          onSubmit={handleSubmitCollection}
        />
      )}
    </React.Fragment>
  );
}
